/**
 * Sprint 6 C3 (EFISSUE-820) — projects migration.
 *
 * One-time migration that moves `relay` and `juju` from the
 * `pinned_filters` array into a new `projects` array on
 * `relay_workspaces/default`. Runs on first load when `projects`
 * is empty AND `pinned_filters` still contains either tag.
 *
 * Pure function — no Firestore calls. App.tsx invokes
 * `computeMigration` against the live snapshot, then applies the
 * returned ops via a single `setDoc(..., { merge: true })`. This
 * shape is unit-testable and idempotent.
 */
import type { Feature } from "../types";

import type { ProjectDelivery } from "./projectDelivery";

export interface ProjectBackground {
  vision: string;
  description: string;
  targetUsers: string;
  techStack: string;
  constraints: string;
  currentStatus: string;
  /**
   * seq #22907 (amendment to #22898) Step 2 — absolute path to this
   * project's local checkout. Optional: existing background docs predate
   * this field. Consumed by the scheduler/daemon (adk-relay, separate
   * repo) in later steps to spawn agents with the correct cwd.
   */
  working_directory?: string;
  /** track:track-backlog--project-modal-rebuild-three-tab-layout-with-prd-versioning — Tab 1 (Project Setup). */
  gitRepoUrl?: string;
  /** track:track-backlog--project-modal-rebuild-three-tab-layout-with-prd-versioning — Tab 3 (Requirements) freeform text. */
  requirements?: string;
  /** Incremented server-side on every Tab 2 (Background) or Tab 3 (Requirements) save. Absent until the first such save. */
  prd_version?: number;
  /** "{project-name}-prd-v{N}" — mirrors prd_version, written alongside it. */
  prd_label?: string;
  /** Last tab saved — drives which tab the left-nav "↗" deep link opens to. */
  prd_last_tab?: "setup" | "background" | "requirements";
  /**
   * track:feature-intelligence-phase1 (Team Log seq #42191/#42199) — four
   * additive fields backing the Feature Intelligence Layer. All optional;
   * existing background docs predate them.
   */
  /** e.g. "relay", "adk-relay", "estateflow" — which repo dev agents build against for this project. */
  repoName?: string;
  /** Team log seq numbers pinned as reference material for this project. */
  referencedSeqs?: number[];
  /**
   * "draft" = being edited, not yet canonical agent context — agents only
   * receive published project backgrounds (get_project_background masks
   * draft projects as not-found). Absent is treated as "published" for
   * backward compat with every background doc written before this field
   * existed.
   */
  status?: "draft" | "published";
  features?: Feature[];
  /** track:project-record-p2 — optimistic-concurrency counter; server-owned, sent back on every save. */
  version?: number;
  /**
   * track:feature-intelligence-phase3 (addendum #42199, "Project background
   * doc addition") — average passRate across all features with at least one
   * completed Test-A-Feature run, refreshed whenever any feature's
   * featureScore updates (see featureIntelligenceService.ts's
   * refreshFeatureTestScore). Absent until the first run on this project
   * completes.
   */
  featureTestScore?: number;
  /**
   * track:test-environment-and-data-notes (seq #42924) — the URL,
   * workspace, and login context testers use when executing test cases for
   * this project, e.g. "app.easyrelay.app — relay workspace — log in as
   * travis@tber.com". Surfaced to hgv_create_case's context so an agent
   * writing test steps can reference the correct environment. Optional;
   * existing background docs predate this field.
   */
  testEnvironment?: string;
  /**
   * track:test-environment-and-data-notes (seq #42924) — standing setup or
   * preconditions a tester must satisfy before running test cases, e.g.
   * "Ensure the relay project background is published before testing FIL
   * features." Optional; existing background docs predate this field.
   */
  testDataNotes?: string;
  /** PROJECT-MAP-1 (spec #53025) — repo-relative path of the project's codebase map; absent reads as docs/codebase-map.md. */
  codebaseMapPath?: string;
  /**
   * DLV-1 (spec #50001) — what the team does with finished (Built) work before
   * it counts as Delivered. A project with none reads as merge_deploy on the
   * server; the editor shows that default.
   */
  delivery?: ProjectDelivery;
  /**
   * track:pbm-saved-indicator — ISO timestamp of the last write to this doc.
   * Written unconditionally by every save path (saveProjectBackground and
   * saveProjectBackgroundVersioned both stamp it), so it's a reliable "last
   * saved" signal regardless of which save button was used or which tab was
   * active. Powers the persistent "Saved · [time]" indicator in the editor —
   * not itself an editable field. Optional; existing background docs predate
   * this field.
   */
  updatedAt?: string;
}

/** Same logic as the local slugify() in WorkspaceContextEditor.tsx — used to derive
 * the relay_workspaces/{workspaceId}/projects/{slug} doc ID from a project name. */
export function slugify(name: string): string {
  return name.toLowerCase().replace(/[^a-z0-9]+/g, "-");
}

export interface ProjectRecord {
  name: string;
  color: string;
  createdAt: number;
  /**
   * EFISSUE-838 — user-defined sub-filters that compound with the project
   * tag at click time (project tag AND sub-filter tag). Optional so docs
   * written before this field existed remain valid; the empty case is
   * indistinguishable from `[]` everywhere we read it.
   */
  subFilters?: string[];
  /** Project context: description, target users, tech stack, constraints. */
  background?: ProjectBackground;
}

export interface MigrationOps {
  /** Projects to add via `arrayUnion`. */
  projectsToAdd: ProjectRecord[];
  /** Pinned-filter tags to remove via `arrayRemove`. */
  pinsToRemove: string[];
}

const SEED_COLORS: Record<string, string> = {
  relay: "#6366f1",
  juju: "#22c55e",
};

const SEED_TAGS = ["relay", "juju"] as const;

/**
 * Inspect the current workspace doc and return migration ops, or
 * `null` if no migration is needed.
 *
 * Triggers when:
 *   - `existingProjects` is empty (no migration has run yet), AND
 *   - `pinnedFilters` contains at least one of `relay` / `juju`.
 *
 * Anything else (projects already populated, or neither seed tag
 * pinned) returns null — idempotent on repeat calls.
 */
export function computeMigration(
  pinnedFilters: string[],
  existingProjects: ProjectRecord[],
  now: number = Date.now(),
): MigrationOps | null {
  if (existingProjects.length > 0) return null;

  const pinsLower = new Set(pinnedFilters.map((t) => t.toLowerCase()));
  const seedsPresent = SEED_TAGS.filter((t) => pinsLower.has(t));
  if (seedsPresent.length === 0) return null;

  return {
    projectsToAdd: seedsPresent.map((name) => ({
      name,
      color: SEED_COLORS[name],
      createdAt: now,
    })),
    pinsToRemove: [...seedsPresent],
  };
}
